import { Shield, Zap, Target, CheckCircle, HeadphonesIcon, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InteractiveHelpCenter from "./InteractiveHelpCenter";
import AutomationCycleAnimation from "./AutomationCycleAnimation";
import IndustryExpertiseBento from "./IndustryExpertiseBento";
import PrecisionStatsAnimation from "./PrecisionStatsAnimation";

const DecarbonwizeFeaturesSection = () => {
  const { t } = useLanguage();
  
  const tabs = [
    {
      id: "automation",
      title: t("features.automation"),
      icon: Zap,
      subtitle: t("features.automationSubtitle"),
      bullets: [t("features.automationBullet1"), t("features.automationBullet2"), t("features.automationBullet3")],
      learnMore: t("features.automationLearnMore"),
      image: null,
      isAutomation: true
    },
    {
      id: "compliance",
      title: t("features.compliance"),
      icon: Shield,
      subtitle: t("features.complianceSubtitle"),
      bullets: [t("features.complianceBullet1"), t("features.complianceBullet2"), t("features.complianceBullet3")],
      learnMore: t("features.complianceLearnMore"),
      image: null,
      isCompliance: true
    },
    {
      id: "precision",
      title: t("features.precision"),
      icon: Target,
      subtitle: t("features.precisionSubtitle"),
      bullets: [t("features.precisionBullet1"), t("features.precisionBullet2"), t("features.precisionBullet3")],
      learnMore: t("features.precisionLearnMore"),
      isPrecision: true
    },
    {
      id: "support",
      title: t("features.support"),
      icon: HeadphonesIcon,
      subtitle: t("features.supportSubtitle"),
      bullets: [t("features.supportBullet1"), t("features.supportBullet2"), t("features.supportBullet3")],
      learnMore: t("features.supportLearnMore"),
      image: null,
      isInteractive: true
    }
  ];

  return (
    <section id="features" className="py-24 lg:py-32 bg-background relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-br from-[#166a9c]/20 to-transparent rounded-full blur-3xl opacity-30" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16 lg:mb-20">
          <p className="text-base sm:text-lg text-muted-foreground mb-4 sm:mb-6">
            {t("features.whyTitle")}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#166a9c] mb-4 px-2">
            <span className="block">{t("features.descriptionLine1")}</span>
          </h2>
        </div>

        {/* Tabbed Feature Section */}
        <Tabs defaultValue="automation" className="w-full">
          <TabsList className="grid grid-cols-2 sm:flex w-full max-w-2xl mx-auto mb-8 sm:mb-12 bg-[#f9fafb] border border-[#166a9c] p-1.5 sm:p-2 rounded-2xl sm:rounded-full h-auto gap-1 sm:gap-0">
            {tabs.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center justify-center gap-1 sm:gap-2 text-[#166a9c] data-[state=active]:bg-[#166a9c] data-[state=active]:text-white font-medium text-xs sm:text-base md:text-lg rounded-xl sm:rounded-full py-2.5 sm:py-4 px-3 sm:px-8 transition-all duration-300 sm:flex-1"
              >
                <span>{tab.title}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map(tab => (
            <TabsContent key={tab.id} value={tab.id} className="mt-0">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div className="space-y-6">
                  <h3 className="text-3xl sm:text-4xl font-bold text-foreground">
                    {tab.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-xl">
                    {tab.subtitle}
                  </p>
                  <ul className="space-y-4">
                    {tab.bullets.map((bullet, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#166a9c] mt-1 flex-shrink-0" />
                        <span className="text-muted-foreground text-lg">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Learn more link */}
                  {tab.id === 'automation' && (
                    <a href="/#" className="inline-flex items-center gap-2 text-[#166a9c] hover:underline group text-base font-bold">
                      {t("features.automationLearnMore")}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  )}
                  {tab.id === 'compliance' && (
                    <a href="/#vvb-auditors" className="inline-flex items-center gap-2 text-[#166a9c] hover:underline group text-base font-bold">
                      {t("features.complianceLearnMore")}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  )}
                  {tab.id !== 'automation' && tab.id !== 'compliance' && 'learnMore' in tab && tab.learnMore && (
                    <a href="/#" className="inline-flex items-center gap-2 text-[#166a9c] hover:underline group text-base font-bold">
                      {tab.learnMore}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  )}
                </div>

                <div className="relative">
                  {'isInteractive' in tab && tab.isInteractive ? (
                    <>
                      <InteractiveHelpCenter />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#166a9c]/30 rounded-full opacity-30 blur-3xl -z-10" />
                    </>
                  ) : 'isAutomation' in tab && tab.isAutomation ? (
                    <div className="h-[300px] lg:h-[400px]">
                      <AutomationCycleAnimation />
                    </div>
                  ) : 'isCompliance' in tab && tab.isCompliance ? (
                    <IndustryExpertiseBento />
                  ) : 'isPrecision' in tab && tab.isPrecision ? (
                    <PrecisionStatsAnimation />
                  ) : null}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default DecarbonwizeFeaturesSection;
